// KeyValueCollector.jsx
import { Button, Card, Input, Row, Col, Space } from "antd";
import { useEffect, useState } from "react";
import {
  DeleteOutlined,
  PlusOutlined,
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  DownOutlined,
  UpOutlined,
} from "@ant-design/icons";
import { safeParse } from "../../helpers/helper";

const KeyValueCollector = ({id, config, onSave, title, data }) => {
  const [keyValues, setKeyValues] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [more, setMore] = useState(true);

  const setKeyValue = (index, key, value) => {
    const keyValuesTemp = [...keyValues];
    keyValuesTemp[index] = [key, value];
    setKeyValues(keyValuesTemp);
  };

  const deleteKeyValue = (index) => {
    var keyValuesTemp = [...keyValues];
    keyValuesTemp.splice(index, 1);
    setKeyValues(keyValuesTemp);
  };

  const handleSave = () => {
    onSave(keyValues);
    setEditMode(false);
  };

  const handleCancel = () => {
    // Reset to original data
    if (data) {
      const parsedData = typeof data === "string" ? safeParse(data) : data;
      setKeyValues(parsedData[config] || []);
    }
    setEditMode(false);
  };

  const handleAdd = () => {
    if (!editMode) return;
    setKeyValues(
      keyValues.length ? [...keyValues, ["", ""]] : [["Key1", "Value1"]]
    );
  };

  useEffect(() => {
    if (data) {
      const parsedData = typeof data === "string" ? safeParse(data) : data;
      setKeyValues(parsedData[config] || []);
    }
  }, [data, config]);

  // Extra actions for expanded view
  const expandedExtra = (
    <Space split={<span className="text-gray-300">|</span>} size={0}>
      {editMode && (
        <a
          className="text-sm font-medium text-blue-500 hover:text-blue-600 px-2 py-1 rounded-md hover:bg-blue-50 transition-all cursor-pointer"
          onClick={handleAdd}
        >
          <PlusOutlined className="mr-1" />
          Add
        </a>
      )}
      <a
        className="text-sm font-medium text-blue-500 hover:text-blue-600 px-2 py-1 rounded-md hover:bg-blue-50 transition-all cursor-pointer"
        onClick={editMode ? handleSave : () => setEditMode(true)}
      >
        {editMode ? (
          <>
            <SaveOutlined className="mr-1" />
            Save
          </>
        ) : (
          <>
            <EditOutlined className="mr-1" />
            Edit
          </>
        )}
      </a>
      <a
        className="text-sm font-medium text-blue-500 hover:text-blue-600 px-3 py-1 rounded-md hover:bg-blue-50 transition-all cursor-pointer"
        onClick={() => {
          if (editMode) {
            handleCancel();
          } else {
            setMore(false);
          }
        }}
      >
        {editMode ? (
          <>
            <CloseOutlined className="mr-1" />
            Cancel
          </>
        ) : (
          <>
            <UpOutlined className="mr-1" />
            Less
          </>
        )}
      </a>
    </Space>
  );

  // Extra action for collapsed view
  const collapsedExtra = (
    <a
      className="text-sm font-medium text-blue-500 hover:text-blue-600 px-3 py-1 rounded-md hover:bg-blue-50 transition-all cursor-pointer"
      onClick={() => setMore(true)}
    >
      <DownOutlined className="mr-1" />
      More
    </a>
  );

  return (
    <Card
      id={id}
      key={title}
      title={<span className="text-base font-semibold text-gray-800">{title}</span>}
      className="m-1 mt-4 rounded-lg shadow-sm border border-gray-200"
      extra={more ? expandedExtra : collapsedExtra}
    >
      {keyValues && more && keyValues.length > 0 ? (
        keyValues.map((item, index) => (
          <Row
            key={config + index}
            className="mb-2 p-1 bg-gray-50 rounded-md border border-gray-100 hover:bg-gray-100 hover:border-gray-200 transition-all"
            gutter={[8, 0]}
            align="middle"
          >
            <Col span={editMode ? 7 : 8}>
              {editMode ? (
                <Input
                  className="rounded-md"
                  placeholder="Key"
                  value={item[0]}
                  onChange={(e) => setKeyValue(index, e.target.value, item[1])}
                />
              ) : (
                <div className="text-sm text-gray-600 font-medium px-3 py-2 flex items-center">
                  {item[0]}
                </div>
              )}
            </Col>

            <Col span={editMode ? 14 : 16}>
              {editMode ? (
                <Input
                  className="rounded-md"
                  placeholder="Value"
                  value={item[1]}
                  onChange={(e) => setKeyValue(index, item[0], e.target.value)}
                />
              ) : (
                <div className="float-right text-sm text-gray-500 font-semibold px-3 py-2 flex items-center">
                  {item[1]}
                </div>
              )}
            </Col>

            {editMode && (
              <Col span={3} className="text-right">
                <Button
                  size="small"
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  className="rounded-md text-red-500 hover:text-red-600 hover:bg-red-50 border-red-100"
                  onClick={() => deleteKeyValue(index)}
                />
              </Col>
            )}
          </Row>
        ))
      ) : (
        more && (
          <div className="text-center py-10 text-gray-400 text-sm">
            {editMode ? "Click 'Add' to create a new entry" : "No entries yet"}
          </div>
        )
      )}
    </Card>
  );
};

export default KeyValueCollector;
